// NPM packages
import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  TextField,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import { observer } from 'mobx-react-lite';
import { useNavigate } from 'react-router-dom';
// All other imports
import { useServices } from 'services';
import { MembershipTier } from 'types/SignUpData';
import TierCard, {
  TIERS,
  TIER_ORDER,
} from 'components/pages/UserMemberships/TierCard';


interface MembershipTierCardProps {
  currentTier?: string;
  currentOrcidId?: string;
}

function MembershipTierCard({
  currentTier,
  currentOrcidId,
}: MembershipTierCardProps): React.ReactElement {
  const { authService } = useServices();
  const navigate = useNavigate();

  const [upgradeDialogOpen, setUpgradeDialogOpen] = useState(false);
  const [selectedUpgrade, setSelectedUpgrade] = useState<MembershipTier | null>(null);
  const [requestSent, setRequestSent] = useState(false);
  const [upgradeJustification, setUpgradeJustification] = useState('');
  const [justificationError, setJustificationError] = useState<string | undefined>(undefined);
  const [showDetailsForm, setShowDetailsForm] = useState(false);
  const [orcidError, setOrcidError] = useState<string | undefined>(undefined);

  const tier = (currentTier as MembershipTier) || 'Tier 1';
  const currentTierInfo = TIERS.find((t) => t.value === tier) || TIERS[0];
  const currentTierIndex = TIER_ORDER.indexOf(tier);
  const upgradeTiers = TIERS.filter(
    (t) => TIER_ORDER.indexOf(t.value) > currentTierIndex
  );
  const isMaxTier = upgradeTiers.length === 0;
  const hasPendingUpgrade = authService.user?.requestStatus === 'UPGRADE_REQUEST_PENDING';

  const resetDialog = (): void => {
    setUpgradeDialogOpen(false);
    setSelectedUpgrade(null);
    setRequestSent(false);
    setShowDetailsForm(false);
    setUpgradeJustification('');
    setJustificationError(undefined);
    setOrcidError(undefined);
  };

  const handleContinue = (): void => {
    if (
      selectedUpgrade === 'Tier 3' &&
      !(authService.user?.orcidId || currentOrcidId || '').trim()
    ) {
      setOrcidError(
        'Tier 3 requires an ORCID ID. Please update your profile with your ORCID ID before submitting an upgrade request.'
      );
      return;
    }
    setOrcidError(undefined);
    setShowDetailsForm(true);
  };

  const handleRequestUpgrade = async (): Promise<void> => {
    if (!upgradeJustification.trim()) {
      setJustificationError('A justification is required for tier upgrade requests.');
      return;
    }
    await authService.requestTierUpgrade({
      requestedTier: selectedUpgrade!,
      justification: upgradeJustification.trim(),
      orcidId: selectedUpgrade === 'Tier 3'
        ? (authService.user?.orcidId || currentOrcidId || undefined)
        : undefined,
      currentTier: tier,
    });
    setRequestSent(true);
  };

  return (
    <>
      <Box>
        <Box sx={{ maxWidth: 360, mb: 2 }}>
          <TierCard
            tier={currentTierInfo}
            userCurrentTier={tier}
            showSelectButton={false}
          />
        </Box>

        {!isMaxTier && (
          <Box sx={{ mt: 2.5 }}>
            <Divider sx={{ mb: 2 }} />
            {hasPendingUpgrade ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <HourglassTopIcon fontSize="small" />
                <Typography variant="body2">
                  Your tier upgrade request is pending admin review.
                </Typography>
              </Box>
            ) : (
              <Button
                variant="contained"
                startIcon={<ArrowUpwardIcon />}
                onClick={() => setUpgradeDialogOpen(true)}
              >
                Request Tier Upgrade
              </Button>
            )}
          </Box>
        )}
        {isMaxTier && (
          <Box sx={{ mt: 2.5 }}>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="body2" color="text.secondary" fontStyle="italic">
              You have the highest membership tier.
            </Typography>
          </Box>
        )}
      </Box>

      <Dialog open={upgradeDialogOpen} onClose={resetDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {requestSent
            ? 'Request Submitted'
            : showDetailsForm
            ? 'Tier Upgrade — Justification'
            : 'Request Tier Upgrade'}
        </DialogTitle>
        <DialogContent>
          {requestSent ? (
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <CheckCircleIcon sx={{ fontSize: 48, color: 'success.main', mb: 1 }} />
              <Typography variant="h6" gutterBottom>
                Upgrade Request Submitted!
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Your request to upgrade to{' '}
                <strong>{TIERS.find((t) => t.value === selectedUpgrade)?.title}</strong>{' '}
                has been submitted. An administrator will review your request.
              </Typography>
            </Box>
          ) : showDetailsForm ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Tier upgrade requests require justification. Please describe why you
                need a higher tier.
              </Typography>
              <TextField
                label="Justification"
                placeholder="Please provide justification for your tier upgrade request."
                value={upgradeJustification}
                onChange={(e) => {
                  setUpgradeJustification(e.target.value);
                  if (justificationError) setJustificationError(undefined);
                }}
                error={!!justificationError}
                helperText={justificationError}
                multiline
                rows={4}
                fullWidth
                size="small"
                required
              />
            </Box>
          ) : (
            <>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Select the tier you would like to upgrade to. Your request will be
                reviewed by an administrator.
              </Typography>
              {orcidError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {orcidError}
                </Alert>
              )}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    md: `repeat(${TIERS.length}, 1fr)`,
                  },
                  gap: 2,
                  alignItems: 'stretch',
                }}
              >
                {TIERS.map((t) => {
                  const isUpgradeable = TIER_ORDER.indexOf(t.value) > currentTierIndex;
                  return (
                    <TierCard
                      key={t.value}
                      tier={t}
                      userCurrentTier={tier}
                      isSelected={selectedUpgrade === t.value}
                      onSelect={isUpgradeable ? (v) => setSelectedUpgrade(v) : undefined}
                      showSelectButton={isUpgradeable}
                      highlightCurrentTier={false}
                    />
                  );
                })}
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          {requestSent ? (
            <Button variant="contained" onClick={resetDialog}>
              Done
            </Button>
          ) : showDetailsForm ? (
            <>
              <Button
                variant="contained"
                color="secondary"
                onClick={() => {
                  setShowDetailsForm(false);
                  setJustificationError(undefined);
                }}
              >
                Back
              </Button>
              <Button variant="contained" onClick={handleRequestUpgrade}>
                Submit Upgrade Request
              </Button>
            </>
          ) : (
            <>
              <Button variant="contained" color="secondary" onClick={resetDialog}>
                Cancel
              </Button>
              <Button
                variant="contained"
                disabled={!selectedUpgrade}
                onClick={handleContinue}
              >
                Continue
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
}

export default observer(MembershipTierCard);